import { useEffect, useState } from 'react';

/**
 * The prototype's decorative bar pattern — used until the real peaks are
 * decoded, and as the fallback if decoding is unavailable (older Safari, an
 * unsupported container). Values are normalised 0–1.
 */
export function syntheticPeaks(barCount: number): number[] {
  return Array.from({ length: barCount }, (_, i) =>
    Math.abs(Math.sin(i * 1.27) * Math.cos(i * 0.41)),
  );
}

function extractPeaks(buffer: AudioBuffer, barCount: number): number[] {
  const samples = buffer.getChannelData(0);
  const bucketSize = Math.max(1, Math.floor(samples.length / barCount));
  const peaks: number[] = [];
  let loudest = 0;

  for (let bar = 0; bar < barCount; bar += 1) {
    const start = bar * bucketSize;
    const end = Math.min(start + bucketSize, samples.length);
    let sumSquares = 0;
    for (let i = start; i < end; i += 1) sumSquares += samples[i] * samples[i];
    const rms = Math.sqrt(sumSquares / Math.max(1, end - start));
    loudest = Math.max(loudest, rms);
    peaks.push(rms);
  }

  if (loudest === 0) return syntheticPeaks(barCount);
  return peaks.map((p) => p / loudest);
}

type AudioContextConstructor = typeof AudioContext;

function getAudioContextCtor(): AudioContextConstructor | undefined {
  if (typeof window === 'undefined') return undefined;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextConstructor })
      .webkitAudioContext
  );
}

/**
 * Normalised (0–1) bar heights for a recorded clip. Falls back to the
 * prototype's pattern whenever the audio cannot be decoded, so the waveform
 * always renders something to fill as playback progresses (AC 5).
 */
export function useAudioPeaks(blob: Blob | null, barCount = 34): number[] {
  const [peaks, setPeaks] = useState<number[]>(() => syntheticPeaks(barCount));

  useEffect(() => {
    setPeaks(syntheticPeaks(barCount));
    if (!blob) return;

    const Ctor = getAudioContextCtor();
    if (!Ctor) return;

    let cancelled = false;
    let context: AudioContext | null = null;

    (async () => {
      try {
        const data = await blob.arrayBuffer();
        context = new Ctor();
        const buffer = await context.decodeAudioData(data);
        if (!cancelled) setPeaks(extractPeaks(buffer, barCount));
      } catch (err) {
        // Not fatal — the synthetic pattern still animates with playback.
        console.warn('useAudioPeaks: could not decode the recording', err);
      } finally {
        await context?.close().catch(() => {});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blob, barCount]);

  return peaks;
}
